import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { Icon } from "@/components/ui/Icon";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { EmptyState, ErrorState } from "@/components/ui/States";
import { request } from "@/lib/api/client";
import { lastDays, useDashboard } from "@/lib/api/dashboard";
import { useList } from "@/lib/api/queries";
import { usePeople } from "@/lib/api/people";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "@/lib/auth/useSession";
import { PERM } from "@/lib/auth/rbac";
import { dateTime, relative } from "@/lib/format";
import { roleLabel, STATUS_LABELS } from "@/lib/labels";
import { dispatchRank, moveItem, rankPriorities } from "./priority";
import "./tasks.css";

/**
 * Topshiriqlar navbati.
 *
 * Ilgari bu ekran umumiy HISOBOT edi: u read modeldan o'qirdi,
 * manbadan orqada qolardi va `id` bo'yicha saralanardi — ya'ni
 * ro'yxat navbatni KO'RSATMASDI. Dispetcher esa aynan navbatni
 * boshqaradi.
 *
 * Endi ro'yxat task-service'dan `dispatch_rank, priority, created_at`
 * tartibida keladi — xodim TSD da aynan shu ketma-ketlikda oladi — va
 * qatorlarni sudrab tartibni o'zgartirish mumkin (faqat bitta navbat
 * guruhi ichida, qarang `dispatchRank`).
 *
 * Turlar ARALASHTIRILMAYDI: ustuvorlik bir xil roldagi ishlar
 * orasida taqqoslanadi (server navbatni rol bo'yicha filtrlaydi),
 * shuning uchun terishni qabul bilan yonma-yon qo'yib tartiblash
 * hech narsa anglatmasdi.
 */

interface Task {
  id: string;
  task_type: string;
  status: string;
  priority: number;
  required_role: string;
  assignee_id?: string;
  source_process?: string;
  order_id?: string;
  document_number?: string;
  waiting_for?: string;
  version: number;
  created_at: string;
}

/**
 * Ko'chirib bo'lmaydigan holatlar (STA-004).
 *
 * Boshlangan ishni qayta rejalashtirish mumkin emas: avval fizik
 * fakt kelishtiriladi. Server ham shuni rad etadi, lekin dastak
 * ko'rsatilsa xodim buni faqat bosgandan keyin bilib olardi.
 */
const LOCKED = new Set(["IN_PROGRESS", "DISCREPANCY"]);

const QUEUE_LIMIT = 200;
/** Navbat yangilanish oralig'i: yakunlangan ish shuncha vaqtda oynadan chiqadi. */
const QUEUE_REFRESH_MS = 5000;

const typeLabel = (value: string) => STATUS_LABELS[value] ?? value;

/** Lug'atda yo'q kodni xom holda ko'rsatish o'rniga kichik harf bilan. */
const label = (value: string) => STATUS_LABELS[value] ?? value.toLowerCase().replace(/_/g, " ");

/**
 * Qator sarlavhasi: ish QAYERDAN kelgani.
 *
 * Tur yozilmaydi — u tanlangan chipda turibdi va har bir qatorda
 * takrorlansa ro'yxat bir xil so'zdan iborat bo'lib qolardi.
 */
function taskTitle(row: Task): string {
  // 1C raqami — dispetcher qog'ozdagi nakladnoyni shu bilan topadi.
  // Ichki ID ning boshi hech kimga hech narsa demasdi.
  if (row.document_number) return row.document_number;
  if (row.order_id) return `Buyurtma ${row.order_id.slice(0, 8)}`;
  if (row.source_process) return label(row.source_process);
  return typeLabel(row.task_type);
}

export function Tasks() {
  const session = useSession();
  const client = useQueryClient();
  const canReplan = session.can(PERM.taskReplan);

  // Tur ro'yxati va sonlari boshqaruv panelining agregatidan:
  // u allaqachon keshda va bir xil manbadan quriladi. Davr bu
  // yerda ahamiyatsiz — chiplar OCHIQ topshiriqlarni sanaydi va
  // ular davrdan mustaqil.
  const summary = useDashboard(lastDays(1), null);
  const types = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of summary.data?.open ?? []) {
      counts.set(row.task_type, (counts.get(row.task_type) ?? 0) + row.count);
    }
    return [...counts.entries()]
      .map(([task_type, count]) => ({ task_type, count }))
      .sort((left, right) => right.count - left.count || left.task_type.localeCompare(right.task_type));
  }, [summary.data]);

  const [type, setType] = useState<string>("");
  useEffect(() => {
    if (!type && types[0]) setType(types[0].task_type);
  }, [types, type]);

  // Sudralgan tartib SERVERGA DARHOL yuborilmaydi: har bir
  // o'zgarish auditga sabab bilan tushadi, sabab esa butun
  // qayta tartiblash uchun BITTA marta so'raladi.
  const [draft, setDraft] = useState<Task[] | null>(null);

  // Navbat o'zi yangilanadi: yakunlangan topshiriq server javobidan
  // chiqadi va shu bilan oynadan ham yo'qoladi (ilgari sahifa qayta
  // ochilguncha turib qolardi). Tartib qoralamasi ochiq bo'lsa
  // yangilanish to'xtaydi — aks holda u dispetcher qo'lida o'chardi.
  const queue = useList<Task>(
    "task",
    "/api/v1/tasks/queue",
    { task_type: type, limit: QUEUE_LIMIT },
    { enabled: type !== "", refetchInterval: draft ? undefined : QUEUE_REFRESH_MS },
  );
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Tur almashsa yoki ro'yxat qayta yuklansa qoralama bekor
  // bo'ladi: eskirgan tartibni saqlash boshqa birovning
  // o'zgarishini bosib ketardi.
  useEffect(() => {
    setDraft(null);
    setReason("");
    setError(undefined);
  }, [type]);

  const rows = draft ?? queue.data?.items ?? [];
  // Faol ishni KIM olgani — dispetcher kimdan so'rashini biladi.
  const people = usePeople(rows.map((row) => row.assignee_id));
  // Guruh — SERVERDAGI (asl) ustuvorlikdan: draft qatorlari asl qiymatda.
  const changes = draft
    ? rankPriorities(draft.map((row) => ({ id: row.id, priority: row.priority, rank: dispatchRank(row) })))
    : new Map<string, number>();

  const drop = (to: number) => {
    const from = dragFrom;
    setDragFrom(null);
    setDragOver(null);
    if (from === null || from === to) return;

    const source = draft ?? queue.data?.items ?? [];
    // Qulflangan qatorga yoki undan ko'chirish yo'q.
    if (LOCKED.has(source[from]?.status ?? "") || LOCKED.has(source[to]?.status ?? "")) return;
    // Guruhlar orasida ko'chirish server tartibini o'zgartirmasdi.
    if (dispatchRank(source[from]!) !== dispatchRank(source[to]!)) {
      setError("Bu qator boshqa navbat guruhida (masalan vozvrat yoki shoshilinch) — faqat o'z guruhi ichida ko'chiriladi");
      return;
    }
    setError(undefined);
    setDraft(moveItem(source, from, to));
  };

  const save = () => {
    if (!draft || changes.size === 0 || reason.trim() === "") return;
    setSaving(true);
    setError(undefined);

    // Ketma-ket: server har bir topshiriqni versiya bilan
    // qulflaydi va parallel so'rovlar bir-birini bekor qilardi.
    const queueChanges = draft.filter((row) => changes.has(row.id));
    const send = async () => {
      for (const row of queueChanges) {
        await request("task", `/api/v1/tasks/${row.id}/replan`, {
          method: "POST",
          body: {
            priority: changes.get(row.id),
            expected_version: row.version,
            reason: reason.trim(),
          },
        });
      }
    };

    send()
      .then(() => {
        setDraft(null);
        setReason("");
        void client.invalidateQueries({ queryKey: ["task"] });
        void client.invalidateQueries({ queryKey: ["report"] });
      })
      .catch((cause: unknown) =>
        setError(
          `Tartib to'liq saqlanmadi: ${
            cause instanceof Error ? cause.message : String(cause)
          }. Ro'yxatni yangilang va qaytadan urinib ko'ring.`,
        ),
      )
      .finally(() => setSaving(false));
  };

  return (
    <>
      <PageHeader
        title="Topshiriqlar"
        hint={
          canReplan
            ? "Navbat tartibi: yuqoridagi topshiriq birinchi bajariladi. Qatorni sudrab tartibni o'zgartiring — o'zgarish sabab bilan saqlanadi."
            : "Navbat tartibi: yuqoridagi topshiriq birinchi bajariladi."
        }
        actions={
          <Link to="/reports" className="btn btn-text btn-sm">
            Hisobot
            <Icon name="chevronRight" size={14} />
          </Link>
        }
      />

      {types.length > 0 && (
        <div className="type-chips" role="tablist" aria-label="Topshiriq turi">
          {types.map((item) => (
            <button
              key={item.task_type}
              type="button"
              role="tab"
              aria-selected={item.task_type === type}
              className={`type-chip${item.task_type === type ? " active" : ""}`}
              data-type={item.task_type}
              onClick={() => setType(item.task_type)}
            >
              <span>{typeLabel(item.task_type)}</span>
              <span className="type-chip-count">{item.count}</span>
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="notice" role="alert">
          <Icon name="alert" size={16} />
          <span>{error}</span>
        </div>
      )}

      {queue.error ? (
        <ErrorState error={queue.error} onRetry={() => void queue.refetch()} />
      ) : queue.isLoading || summary.isLoading ? (
        <div className="skeleton" style={{ height: 320 }} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="task"
          title="Navbat bo'sh"
          hint="Bu turda ochiq topshiriq yo'q. Yangi ish paydo bo'lishi bilan u shu yerda ko'rinadi."
        />
      ) : (
        <ol className="queue">
          {rows.map((row, index) => {
            const locked = LOCKED.has(row.status);
            const draggable = canReplan && !locked;
            // Ustuvorlik RAQAMI ko'rsatilmaydi: dispetcherga
            // tizimning ichki qiymati emas, TARTIB kerak — u chap
            // tomondagi o'rin raqamida va qatorlar ketma-ketligida
            // turibdi. O'zgargan qator rang bilan ajratiladi.
            const changed = changes.has(row.id);

            return (
              <li
                key={row.id}
                className={
                  "queue-row" +
                  (locked ? " locked" : "") +
                  (dragOver === index ? " over" : "") +
                  (dragFrom === index ? " dragging" : "") +
                  (changed ? " changed" : "")
                }
                draggable={draggable}
                onDragStart={() => setDragFrom(index)}
                onDragEnd={() => {
                  setDragFrom(null);
                  setDragOver(null);
                }}
                onDragOver={(event) => {
                  if (dragFrom === null || locked) return;
                  // Busiz brauzer tashlashga ruxsat bermaydi.
                  event.preventDefault();
                  setDragOver(index);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  drop(index);
                }}
              >
                <span className="queue-handle" aria-hidden="true">
                  {draggable ? (
                    <Icon name="grip" size={16} />
                  ) : (
                    <Icon name="lock" size={14} />
                  )}
                </span>

                <span className="queue-position num">{index + 1}</span>

                <span className="queue-main">
                  {/* Sarlavhada TUR TAKRORLANMAYDI — u yuqoridagi
                      chipda turibdi. Qatorlarni bir-biridan
                      ajratadigan narsa: ish qayerdan kelgani. */}
                  <span className="queue-title">{taskTitle(row)}</span>
                  <span className="queue-sub">
                    {roleLabel(row.required_role)}
                    {row.assignee_id
                      ? ` · ${people.get(row.assignee_id) ?? "biriktirilgan"}`
                      : " · hech kim olmagan"}
                    {row.waiting_for ? ` · ${label(row.waiting_for)} kutmoqda` : ""}
                  </span>
                </span>

                <StatusBadge value={row.status} />

                <span className="queue-age" title={dateTime(row.created_at)}>
                  {relative(row.created_at)}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {queue.data?.has_more && (
        <p className="field-hint">
          Birinchi {QUEUE_LIMIT} ta ko'rsatilgan. Navbat bundan uzun bo'lsa, tartibni
          qo'lda boshqarish o'rniga ustuvorlikni manba jarayonda belgilash kerak.
        </p>
      )}

      {/* Saqlash paneli faqat haqiqiy o'zgarish bo'lganda: sudrab
          o'z joyiga qaytarilgan qator hech narsani o'zgartirmaydi. */}
      {changes.size > 0 && (
        <div className="queue-save glass" role="region" aria-label="Tartibni saqlash">
          <span className="queue-save-count">
            {changes.size} ta topshiriq ustuvorligi o'zgaradi
          </span>
          <input
            className="input"
            placeholder="Sabab — auditda qoladi"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <button
            type="button"
            className="btn btn-text"
            disabled={saving}
            onClick={() => {
              setDraft(null);
              setReason("");
            }}
          >
            Bekor qilish
          </button>
          <button
            type="button"
            className="btn btn-filled"
            disabled={saving || reason.trim() === ""}
            onClick={save}
          >
            {saving ? "Saqlanmoqda…" : "Saqlash"}
          </button>
        </div>
      )}
    </>
  );
}
